// utils/permissions.js
//
// Readers over the server-computed `collection.myAccess` annotation — the ONE
// way the UI learns what the caller may do. The server enforces everything;
// these helpers exist so the UI can render honestly (hide manage affordances
// from readers, show read-only forms) instead of letting users click into
// 403s.
//
// myAccess shape (docs/SCHEMA.md in keepr-api):
//   { role: 'owner'|'manage'|'write'|'read'|null,
//     isOwner: boolean,
//     isAdmin?: boolean,
//     cardRoles: { [cardId]: 'read'|'write' },   // card-scoped grants, descendants expanded
//     global?: boolean,    // a GLOBAL collection this caller reads as such:
//                          // never a role — a pure consumer has role null and
//                          // may read; writes nothing
//     linked?: { via, whole, cardIds, queryGrants, allowCopies } }
//                          // read THROUGH A COLLECTION LINK (keepr-api
//                          // § Collection links): role null, never a write
//
// LEGACY fallback: a response from a pre-grants API (no myAccess) derives
// owner/write from ownedBy/sharedWith so the UI keeps working during the
// deploy window. Remove alongside the API's LEGACY-SHAREDWITH retirement.

import { getUserId } from './index';
import { getSharedWith } from './collections';
import { normalizeId } from './ids';

export const ROLE_RANK = { read: 1, write: 2, manage: 3, owner: 4 };

// One vocabulary for role labels everywhere (badges, selects, hints).
export const COLLECTION_ROLE_OPTIONS = [
	{ value: 'read', label: 'Can view' },
	{ value: 'write', label: 'Can edit' },
	{ value: 'manage', label: 'Manager' },
];
// Card-scoped grants carry read/write only (API rule).
export const CARD_ROLE_OPTIONS = COLLECTION_ROLE_OPTIONS.slice(0, 2);

const EMPTY_ACCESS = Object.freeze({ role: null, isOwner: false, isAdmin: false, global: false, cardRoles: Object.freeze({}), linked: null });

export function getMyAccess(collection) {
	if (!collection) return EMPTY_ACCESS;
	if (collection.myAccess && typeof collection.myAccess === 'object') {
		const { role = null, isOwner = false, isAdmin = false, cardRoles, global = false, linked = null } = collection.myAccess;
		return {
			role,
			isOwner,
			isAdmin,
			global: global === true,
			cardRoles: cardRoles || {},
			linked: linked && typeof linked === 'object' ? linked : null,
		};
	}
	// LEGACY fallback (pre-grants API response)
	const userId = getUserId();
	if (collection.ownedBy === userId) {
		return { role: 'owner', isOwner: true, isAdmin: false, cardRoles: {} };
	}
	if (getSharedWith(collection).includes(userId)) {
		return { role: 'write', isOwner: false, isAdmin: false, cardRoles: {} };
	}
	return EMPTY_ACCESS;
}

export function roleRank(collection) {
	const access = getMyAccess(collection);
	if (access.isAdmin) return ROLE_RANK.manage;
	return ROLE_RANK[access.role] || 0;
}

/** Any visibility at all: a collection-level role, a card-scoped grant, or a global collection. */
export function canRead(collection) {
	const access = getMyAccess(collection);
	return roleRank(collection) >= ROLE_RANK.read
		|| Object.keys(access.cardRoles).length > 0
		|| access.isAdmin
		|| access.global;
}

/**
 * A global collection's CONSUMER: someone who reads it only because it is
 * global — no role, no card grant, not an admin. They read every record but
 * private ones and write nothing; the bar offers Follow instead of Settings.
 */
export function isGlobalConsumer(collection) {
	const access = getMyAccess(collection);
	return access.global && !access.role && !access.isAdmin && Object.keys(access.cardRoles).length === 0;
}

/**
 * A reader of this collection ONLY through a collection link (keepr-api
 * § Collection links): no role, no card grant, not an admin — but a
 * `myAccess.linked`. They read the link's records and nothing else: every
 * write, sharing (short links, share links), history, exports and — unless
 * the link lets them keep copies — attachment downloads are refused to
 * them, so the UI must not offer those. (Someone with their own grant here
 * is not one: their grant answers, whatever a link adds.)
 */
export function isLinkOnlyReader(collection) {
	const access = getMyAccess(collection);
	return Boolean(access.linked)
		&& !access.isAdmin
		&& roleRank(collection) === 0
		&& Object.keys(access.cardRoles).length === 0;
}

/**
 * May this caller put a PRIVATE tag on this collection (tags b53)? The
 * private store needs read on the collection of the caller's OWN (keepr-api
 * tagApplicationUtils: `canReadCollectionMeta` without the global or link
 * flags) — a role, a card grant or a filtered grant here, or an admin — so a
 * global collection's consumer and a link-only reader are not offered it.
 */
export function canTagCollectionPrivately(collection) {
	if (!collection) return false;
	return canRead(collection) && !isGlobalConsumer(collection) && !isLinkOnlyReader(collection);
}

/**
 * May this caller pin the collection itself to Home (KPR-1125)? The pin's PUT
 * answers the metadata read of the caller's OWN (keepr-api
 * homeUtils.pinCollectionLink: `assertCollectionMetaRead`, no global, no link
 * reach) — the star's gate, and the charts'.
 */
export function canPinCollection(collection) {
	if (!collection) return false;
	return canRead(collection) && !isGlobalConsumer(collection) && !isLinkOnlyReader(collection);
}

/**
 * May this caller read charts here — the Reports page, a chart, a run (charts
 * A3)? The charts store needs metadata read of the caller's OWN (keepr-api
 * chartUtils: `Access.assertCollectionMetaRead` with no `includeGlobal` and no
 * `includeLinked`) — a role, a card grant or a filtered grant, or an admin.
 * A global collection's consumer and a link-only reader get no charts yet
 * (ruled analytics d14): every chart door answers them the one 404, so
 * Reports is not offered to them.
 */
export function canReadCharts(collection) {
	if (!collection) return false;
	return canRead(collection) && !isGlobalConsumer(collection) && !isLinkOnlyReader(collection);
}

/**
 * May this caller take COPIES of what they read here — downloads,
 * exports? Always, unless they are a link-only reader whose link does not
 * allow copies (`403 copies_not_allowed`).
 */
export function canTakeCopies(collection) {
	if (!isLinkOnlyReader(collection)) return true;
	return getMyAccess(collection).linked?.allowCopies === true;
}

/** Collection-wide write (create/edit/delete items on every card). */
export function canWrite(collection) {
	return roleRank(collection) >= ROLE_RANK.write;
}

/** Settings, sharing, cards, tags — the manager surface. */
export function canManage(collection) {
	return roleRank(collection) >= ROLE_RANK.manage;
}

export function isOwner(collection) {
	return getMyAccess(collection).isOwner === true;
}

/** True when access comes ONLY through card-scoped grants. */
export function isCardScoped(collection) {
	const access = getMyAccess(collection);
	return !access.isAdmin
		&& roleRank(collection) === 0
		&& Object.keys(access.cardRoles).length > 0;
}

/** The card-scoped role for one card ('read' | 'write' | undefined). */
export function cardRole(collection, cardId) {
	return getMyAccess(collection).cardRoles[normalizeId(cardId)];
}

/** May the caller read items of this card? */
export function canReadCard(collection, cardId) {
	return canRead(collection) && (!isCardScoped(collection) || Boolean(cardRole(collection, cardId)));
}

/** May the caller create/edit/delete items of this card? */
export function canWriteCard(collection, cardId) {
	if (canWrite(collection)) return true;
	return cardRole(collection, cardId) === 'write';
}

/** May the caller create items ANYWHERE in the collection (drives the Add item affordance)? */
export function canCreateItems(collection) {
	if (canWrite(collection)) return true;
	return Object.values(getMyAccess(collection).cardRoles).includes('write');
}

/**
 * May the caller add items here NOW — create access (canCreateItems), and
 * the collection is not archived. An archived collection's items are
 * read-only: the API refuses every add there, and every launcher pin (a
 * quick add, a multi add, "add an item of this card here" — keepr-api
 * homeUtils.assertCardLaunchable), whatever the caller's role. What offers an
 * add or a launcher asks this; what only needs the ROLE (a grant summary)
 * asks canCreateItems.
 */
export function canAddItemsNow(collection) {
	return canCreateItems(collection) && collection?.status !== 'archived';
}

/** Card ids accessible to a card-scoped user (descendants already expanded server-side). */
export function accessibleCardIds(collection) {
	return Object.keys(getMyAccess(collection).cardRoles);
}

/** Short human label for role badges. null = render nothing. */
export function roleLabel(collection) {
	const access = getMyAccess(collection);
	if (access.isOwner) return 'Owner';
	if (access.isAdmin) return 'Admin';
	if (access.role === 'manage') return 'Manager';
	if (access.role === 'write') return 'Can edit';
	if (access.role === 'read') return 'Can view';
	if (Object.keys(access.cardRoles).length > 0) return 'Card access';
	return null;
}
